import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL, getAuthHeader } from '@/lib/utils';
import ImageUpload from '@/components/ImageUpload';
import { Plus, Pencil, Trash2, Image, Calendar } from 'lucide-react';

const AdminTrainingGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', image_url: '',
    event_name: '', event_date: '', is_active: true
  });

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API_URL}/training-photos`, { headers: getAuthHeader() });
      setPhotos(res.data);
    } catch (error) {
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPhotos(); }, []);

  const resetForm = () => {
    setForm({ title: '', description: '', image_url: '', event_name: '', event_date: '', is_active: true });
    setEditingPhoto(null);
  };

  const handleEdit = (photo) => {
    setEditingPhoto(photo);
    setForm({
      title: photo.title, description: photo.description || '',
      image_url: photo.image_url, event_name: photo.event_name || '',
      event_date: photo.event_date || '', is_active: photo.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image_url) { toast.error('Title and image are required'); return; }
    try {
      if (editingPhoto) {
        await axios.put(`${API_URL}/training-photos/${editingPhoto.id}`, form, { headers: getAuthHeader() });
        toast.success('Photo updated');
      } else {
        await axios.post(`${API_URL}/training-photos`, form, { headers: getAuthHeader() });
        toast.success('Photo added');
      }
      setDialogOpen(false);
      resetForm();
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to save photo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API_URL}/training-photos/${id}`, { headers: getAuthHeader() });
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  return (
    <div data-testid="admin-training-gallery">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2a5e] font-manrope">Training Gallery</h1>
          <p className="text-gray-500 text-sm mt-1">Upload and manage training event photos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1a2a5e] hover:bg-[#2d4a8e]" data-testid="add-photo-btn">
              <Plus size={16} className="mr-2" /> Add Photo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPhoto ? 'Edit Photo' : 'Add Training Photo'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Photo *</Label>
                <ImageUpload
                  value={form.image_url}
                  onChange={(url) => setForm({...form, image_url: url})}
                  label="Training Photo"
                />
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="NABH Training Workshop" data-testid="photo-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Brief description" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Name</Label>
                  <Input value={form.event_name} onChange={(e) => setForm({...form, event_name: e.target.value})} placeholder="Annual Workshop 2026" />
                </div>
                <div className="space-y-2">
                  <Label>Event Date</Label>
                  <Input value={form.event_date} onChange={(e) => setForm({...form, event_date: e.target.value})} placeholder="March 2026" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({...form, is_active: v})} />
                <Label>Active</Label>
              </div>
              <Button type="submit" className="w-full bg-[#1a2a5e] hover:bg-[#2d4a8e]" data-testid="save-photo-btn">
                {editingPhoto ? 'Update Photo' : 'Add Photo'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : photos.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500">No training photos yet. Click "Add Photo" to get started.</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {photos.map((photo) => (
            <Card key={photo.id} className="overflow-hidden" data-testid={`photo-item-${photo.id}`}>
              <div className="aspect-[4/3] relative">
                <img src={photo.image_url} alt={photo.title} className="w-full h-full object-cover" />
                {!photo.is_active && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded">Inactive</div>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-[#1a2a5e] mb-1">{photo.title}</h3>
                {photo.event_name && <p className="text-sm text-gray-500">{photo.event_name}</p>}
                {photo.event_date && (
                  <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                    <Calendar size={12} /><span>{photo.event_date}</span>
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(photo)} data-testid={`edit-photo-${photo.id}`}>
                    <Pencil size={14} className="mr-1" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(photo.id)} data-testid={`delete-photo-${photo.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTrainingGallery;
